import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output } from '@angular/core';
import { ModalButtons, SpinnerProps } from 'src/app/domain/shared/component/modal-container/modal-container.component';
import { MetricDataRole, MetricDataRoleByNameMap } from '../../dto/MetricData';
import { MetricPeriodWrapper } from '../../dto/MetricGroupsAndPeriods';
import { firstValueFrom, Subject } from 'rxjs';
import { getNaturalNumberArrayFromNTillM } from 'src/app/util/common/math-util';
import { DateService } from 'src/app/util/common/date-service/date.service';
import { LocalDate } from 'src/app/core/pojo/date-variable';
import { NOTIFICATIONS, NotificationsService } from 'src/app/core/service/notifications.service';
import {
  MetricDataRoleForSelector,
  MetricPeriodMonth,
  MetricPeriodYearAndMonth,
  MetricYearPlanning,
  MetricYearSelectionStatus,
  MetricYearsForAddPeriodSelector,
  YearDisabled,
} from '../../dto/ManagePeriodsWrapper';
import { KpiBusinessService } from '../../service/backend/kpi-business.service';
import { takeUntil } from 'rxjs/operators';
import { SharedComponentsModule } from "../../../shared/module/shared-components.module";
import { MetricBulkRequestCompany } from '../../dto/metric-bulk-request';
import { CheckBoxSelector } from '../../../shared/component/checkbox-selector/checkbox-selector.component';

@Component({
  selector: 'app-modal-manage-periods',
  standalone: true,
  templateUrl: './modal-manage-periods.component.html',
  styleUrls: ['./modal-manage-periods.component.scss'],
  imports: [
    SharedComponentsModule,
  ],
})
export class ModalManagePeriodsComponent implements OnChanges, OnDestroy {
  _destroyStream: Subject<void> = new Subject<void>();
  private yearHelper = new YearHelper();
  protected readonly MetricDataRoleMap = MetricDataRoleByNameMap;

  @Input() periodType: MetricDataRole = MetricDataRole.ACTUAL;
  @Input({ required: true }) companies: MetricBulkRequestCompany[];
  @Input({ required: true }) periods: MetricPeriodWrapper;
  @Input({ required: true }) parent: ModalManagePeriodParent;

  @Output() closeModal = new EventEmitter<boolean>();

  companiesForSelector: CheckBoxSelector[];
  selectedCompanies: CheckBoxSelector[] = [];
  selectedPeriod: Date;
  periodTypeForSelector: MetricDataRoleForSelector;

  startingYears: MetricYearsForAddPeriodSelector;
  endingYears: MetricYearsForAddPeriodSelector;

  /**
   * List of MetricYearPlanning objects displayed in the table
   * This list may contain periods from the database, which will be disabled,
   * as well as periods created dynamically based on the range between starting and ending years
   */
  displayedPeriods: MetricYearPlanning[];

  selected: MetricYearSelectionStatus = 'none';
  allSelectedDisabled = false;

  showPeriods: boolean;
  periodsFromDB: MetricPeriodYearAndMonth[];
  applyActivated = false;

  modalButtons: ModalButtons = {
    dismiss: 'Close',
    apply: 'Apply',
  };

  title: string;

  spinnerProps: SpinnerProps = {
    isSpinnerActive: false,
    notActiveOnApply: true,
  };

  constructor(
    private notificationService: NotificationsService,
    private kpiBusinessService: KpiBusinessService) {
  }

  ngOnChanges(): void {
    this.updateViewByPeriodType(this.periodType);
    this.prepareCompaniesForSelector();
  }

  ngOnDestroy(): void {
    this._destroyStream.next();
    this._destroyStream.complete();
  }

  updateViewByPeriodType(periodType: MetricDataRole): void {
    this.resetData();
    this.setCurrentPeriod();
    this.setPeriodTypeForSelector(periodType);
    this.setExistingPeriods();
    this.prepareYears();
    this.updateTitle();
  }

  async confirm(save: boolean): Promise<void> {
    if (!save) {
      this.closeModal.emit(false);
      return;
    }
    const periods = this.preparePeriods();
    // We have a limitation (probably by AWS) to have limited amount of query parameters, which is why
    // we have to chunk our periods and make separate requests
    const chunkedPeriods: LocalDate[][] = [];
    const chunkSize = 30;
    for (let i = 0; i < periods.length; i += chunkSize) {
      chunkedPeriods.push(periods.slice(i, i + chunkSize));
    }

    for (let i = 0; i < this.selectedCompanies.length; i++) {
      for (const chunk of chunkedPeriods) {
        await firstValueFrom(this.kpiBusinessService.addPeriods(chunk, this.selectedCompanies[i].UUID, this.periodType)
          .pipe(takeUntil(this._destroyStream)));
      }
      this.updateTitle((Math.floor((i / this.selectedCompanies.length) * 100)));
    }

    this.notificationService.success(NOTIFICATIONS.METRICS_PERIODS_CREATED_SUCCESS);
    this.closeModal.emit(true);
  }

  setStarting(year: YearDisabled): void {
    this.startingYears.selected.year = year.year;
    const filteredEndingYears = this.startingYears.all.filter((y) => y.year >= year.year);

    let endingYear = this.endingYears.selected.year;
    if (!endingYear || endingYear < year.year) {
      endingYear = filteredEndingYears[0].year;
    }

    this.endingYears = {
      all: filteredEndingYears,
      selected: {
        year: endingYear,
        disabled: false,
      },
    };
    this.updatePeriodVisibility();
  }

  setEnding(year: YearDisabled): void {
    this.endingYears.selected.year = year.year;
    this.updatePeriodVisibility();
  }

  selectAll(): void {
    const initialStatus = this.selected;
    const isSomeYearMissingQuarters = this.displayedPeriods.some((year) => this.yearHelper.isQuarterMissing(year));

    this.displayedPeriods.forEach((year) => {
      if (initialStatus === 'some' && this.yearHelper.isAllSelected(year)) {
        // ignore year if it's selected entirely, while there are not full years
        return;
      }
      if (isSomeYearMissingQuarters && !this.yearHelper.isQuarterMissing(year)) {
        // There are missing quarters, but not in this year :sunglasses:
        return;
      }

      this.selectYear(year);
    });
  }

  /**
   * After selecting a year, we have a couple of options:
   * 1) nothing was selected => select quarters months.
   * 1a) if no quarter month is available, select all available months (e.x. 01 and 02 months).
   * 2) some was selected => select all remaining non-disabled months.
   * 3) all were selected => unselect all.
   * 4) all non-disabled were selected => unselect all.
   *
   * @param year with moths
   */
  selectYear(year: MetricYearPlanning): void {
    const isAllSelected = this.yearHelper.isAllSelected(year);
    const isQuarterMissing = this.yearHelper.isQuarterMissing(year);

    if (isQuarterMissing) {
      // when no checkbox was selected -> select quarters
      this.yearHelper.selectQuartersElseAllMonths(year);
    } else {
      // when not all checkboxes were selected -> select all, otherwise unselect all.
      year.months
        .filter((month) => !month.disabled)
        .forEach((month) => month.selected = !isAllSelected);
    }

    this.checkYearIfAllMonthSelected(year);
  }

  selectMonth(month: MetricPeriodMonth, year: MetricYearPlanning, selected: boolean): void {
    month.selected = selected;
    this.checkYearIfAllMonthSelected(year);
  }

  /**
   * Initializes data based on the period type.
   * Sets the period type for the selector with appropriate name and styling.
   */
  setPeriodTypeForSelector(periodType: MetricDataRole): void {
    const style = periodType === MetricDataRole.ACTUAL ? 'main-8' : 'orange-11';
    this.periodTypeForSelector = {
      name: periodType,
      iconNameAndStylingLeft: `circle.svg; ${style}`,
    };
    this.periodType = periodType;
  }

  updateSelectedCompanies(companies: CheckBoxSelector[]): void {
    this.selectedCompanies = companies.filter((company) => company.chosen);
    this.checkApplyActivated();
  }

  private updateTitle(percentageOfUpdatedCompanies?: number): void {
    if (percentageOfUpdatedCompanies) {
      this.title = `Adding periods: ${percentageOfUpdatedCompanies} out of 100%`;
    } else {
      this.title = 'Add periods';
    }
  }

  /**
   * Sets the current date + 3 months.
   *
   * We need to allow users to add actual periods, which are not restricted by today's month.
   * Current requirement is to allow adding periods up to 3 months in the future.
   * Therefore, this component operates as if it is 3 months in the future.
   * Otherwise, the bigger refactor would be needed.
   */
  private setCurrentPeriod(): void {
    const selectedPeriod = new Date();
    selectedPeriod.setMonth(selectedPeriod.getMonth() + 3);
    this.selectedPeriod = selectedPeriod;
  }

  /**
   * Updates the 'selected' status of a given year based on the selection status of its months.
   * There is a case, when all non-disabled are selected, so the checkbox should be selected as 'all'.
   * Calls 'setAllSelected' to update 'selected' status of the whole table.
   *
   * @param year - The year to check and update.
   */
  private checkYearIfAllMonthSelected(year: MetricYearPlanning): void {
    const isAllSelected = year.months
      .filter((month) => !month.disabled)
      .every((month) => month.selected);
    const isSomeSelected = year.months.some((month) => month.selected);

    this.checkApplyActivated();
    if (isAllSelected) {
      year.selected = 'all';
    } else {
      year.selected = isSomeSelected ? 'some' : 'none';
    }
    this.setAllSelected();
  }

  /**
   * Checks if any month in the displayed periods is selected and at least one company is selected.
   * Updates the 'applyActivated' flag to true if any selected month is found and selected companies has size > 0.
   * Assumes that periods from the database are disabled, and only enabled months are considered new.
   */
  private checkApplyActivated(): void {
    const isPeriodChosen = this.displayedPeriods.some((year) =>
      year.months.some((month) => month.selected && !month.disabled),
    );
    const isCompanySelected = this.selectedCompanies.length > 0;
    this.applyActivated = isCompanySelected && isPeriodChosen;
  }

  /**
   * Checks if all periods are selected and updates the `selected` property accordingly.
   * Calls `checkApplyActivated` to determine if there is at least one period to save.
   *
   * Note:
   * `checkApplyActivated` is called only from this method, as it is the final step in the selection status checking cycle.
   */
  private setAllSelected(): void {
    this.checkApplyActivated();
    if (this.displayedPeriods.every((year) => year.selected === 'all')) {
      this.selected = 'all';
      return;
    }
    const isSomeSelected = this.displayedPeriods.some((year) => year.selected === 'some' || year.selected === 'all');
    this.selected = isSomeSelected ? 'some' : 'none';
  }

  /**
   * Checks if both starting and ending years are selected.
   * Updates the `showPeriods` property based on the selection status.
   * If both are selected, calls `setPeriods` to update the periods.
   */
  private updatePeriodVisibility(): void {
    this.showPeriods = Boolean(this.startingYears.selected.year && this.endingYears.selected.year);
    if (!this.showPeriods) {
      return;
    }
    this.setPeriodsToDisplay();
  }

  /**
   * Creates a list of years based on the selected starting and ending years by following these steps:
   * 1. Calculate the range of years based on the selected starting and ending years.
   * 2. If the list of periods to display is not empty, update them by:
   *    a. Filtering out periods that are not within the calculated range.
   *    b. Adding new periods for years that do not already exist in the current periods.
   *    c. Sorting the periods by year.
   * 3. If the list of periods is empty, initialize them.
   */
  private setPeriodsToDisplay(): void {
    const years = this.calculateYearsRange();
    // If the list of periods is not empty, adjust it to the new range of years
    if (this.displayedPeriods?.length) {
      this.filterExistingPeriods(years);
      this.addNewYears(years);
      this.sortPeriodsByYear();
      this.setDisabledPeriods();
    } else {
      this.initializePeriods(years);
    }
  }

  /**
   * Calculates the range of years based on selected starting and ending years.
   *
   * @returns An array of years from the starting year to the ending year.
   */
  private calculateYearsRange(): number[] {
    return Array.from(
      { length: this.endingYears.selected.year - this.startingYears.selected.year + 1 },
      (_, i) => i + this.startingYears.selected.year,
    );
  }

  /**
   * Filters the displayed periods to keep only those that are either present in the database
   * or fall within the specified new range of years based on the selected new starting and ending periods.
   * After that method, the displayed periods will be adjusted with the new selected periods that are not yet here.
   *
   * @param years - An array of years that defines the new range based on the selected new starting and ending periods.
   */
  private filterExistingPeriods(years: number[]): void {
    // Get the list of years from the existing periods in the database
    const yearsFromDB = this.periodsFromDB.map((period) => period.year);
    // Filter the displayed periods to keep only those that are either in the database
    // or within the new range of selected years
    this.displayedPeriods = this.displayedPeriods.filter((period) =>
      yearsFromDB.includes(period.year) || years.includes(period.year),
    );
  }

  /**
   * Finds new years not already in the displayed periods and adds them to the displayed periods array.
   *
   * @param years - An array of years to include.
   */
  private addNewYears(years: number[]): void {
    // Get the list of years already displayed
    const alreadyAddedPeriods = this.displayedPeriods.map((period) => period.year);

    // Find new years that are not in the database and not already displayed
    const newYears = years.filter((year) =>
      !this.periodsFromDB.some((period) => period.year === year) &&
      !alreadyAddedPeriods.includes(year),
    );

    // Add the new years to the displayed periods
    this.displayedPeriods.push(...newYears.map((year) => new MetricYearPlanning(year)));
  }

  private sortPeriodsByYear(): void {
    this.displayedPeriods.sort((a, b) => a.year - b.year);
  }

  /**
   * Initializes the periods if the list of periods is empty.
   *
   * @param years - The range of years to initialize.
   */
  private initializePeriods(years: number[]): void {
    this.displayedPeriods = years.map((year) => new MetricYearPlanning(year));
    this.setDisabledPeriods();
  }

  /**
   * Prepares a list of years based on the period type and updates the starting and ending years.
   * Steps:
   * 1. Determines the field type ('actual' or 'budget') based on the period type.
   * 2. Sets the minimum year to 2015 and determines the maximum year based on the field type.
   * 3. Generates a range of years from the minimum to the maximum year.
   * 4. Maps existing periods to their respective years.
   * 5. Prepares a list of years with a disabled option if they are present in the existing periods.
   * 6. Sets the starting and ending years with the appropriate values and updates periods visibility.
   */
  private prepareYears(): void {
    const field = this.periodType === MetricDataRole.ACTUAL ? 'actual' : 'budget';
    // Find the minimum and maximum years
    const minYear = 2015;
    const maxYear = field === 'actual' ? this.selectedPeriod.getFullYear() : 2035;
    const years = getNaturalNumberArrayFromNTillM(minYear, maxYear);

    // Map existing periods to their respective years
    const existingPeriods = this.periods[field].map((period) => new Date(period.period).getFullYear());

    const distinctYears = [...new Set(existingPeriods)];
    const yearsWithDisableOption: YearDisabled[] = [];
    // Prepare a list of years with disabled option if they are present in the existing periods
    years.forEach((year) => {
      const preparedYear = {
        year,
        disabled: distinctYears.some((y) => y === year),
      };
      yearsWithDisableOption.push(preparedYear);
    });

    yearsWithDisableOption.sort((a, b) => b.year - a.year);
    // Set starting and ending with the appropriate values
    this.startingYears = {
      all: yearsWithDisableOption,
      selected: {
        year: existingPeriods.length ? existingPeriods[0] : null,
        disabled: false,
      },
    };

    const isSelectedStartingYearProvided = this.startingYears.selected.year !== null;
    // ending years must be higher than selected starting year. e.x. starting: 2022, ending: 2023, 2024, ...
    const endingYears = isSelectedStartingYearProvided ?
      yearsWithDisableOption.filter((year) => year.year > this.startingYears.selected.year)
      : yearsWithDisableOption;
    this.endingYears = {
      all: endingYears,
      selected: {
        year: existingPeriods.length ? existingPeriods[existingPeriods.length - 1] : null,
        disabled: false,
      },
    };

    if (field === 'budget' && !existingPeriods.length) {
      this.setDefaultBudgetYears();
      return;
    }
    this.updatePeriodVisibility();
  }

  /**
   * Sets the default budget years and initializes periods for planning.
   * Updates their visibility
   */
  private setDefaultBudgetYears(): void {
    // Initialize current and next year objects
    const currentYear = {
      year: this.selectedPeriod.getFullYear(),
      disabled: false,
    };

    const nextYear = {
      year: this.selectedPeriod.getFullYear() + 1,
      disabled: false,
    };

    // Set starting and ending years
    this.startingYears.selected = currentYear;
    this.endingYears.selected = nextYear;
    this.updatePeriodVisibility();
  }

  /**
   * Updates the periods with existing data from the groups and periods.
   * 1. Determines the field type ('actual' or 'budget') based on the period type.
   * 2. Maps existing periods to their respective periods.
   * 3. For each year in periods:
   *    a. Filters the existing periods to find those matching the current year.
   *    b. Disables the months in the current year that are present in the existing periods.
   *    c. Updates the `allSelectedDisabled` property if all months are disabled.
   *    d. Updates the `isSomeSelected` property if some months are disabled and not all months are selected.
   */
  private setDisabledPeriods(): void {
    this.displayedPeriods.forEach((year) => {
      const yearPeriods = this.periodsFromDB.filter((period) => period.year === year.year);
      year.months.forEach((month) => {
        yearPeriods.forEach((period) => {
          if (period.month === month.getNumber()) {
            month.disabled = true;
            month.selected = true;
          }
        });
        if (month.getNumber() > this.selectedPeriod.getMonth()
          && year.year >= this.selectedPeriod.getFullYear()
          && this.periodType === MetricDataRole.ACTUAL) {
          month.disabled = true;
          month.selected = false;
        }
      });
      year.allSelectedDisabled = year.months.every((month) => month.disabled);
      this.checkYearIfAllMonthSelected(year);
    });

    this.allSelectedDisabled = this.displayedPeriods.every((year) => year.allSelectedDisabled);
    this.setAllSelected();
  }

  /**
   * Sets the existing periods based on the current period type.
   * Steps:
   * 1. Initializes the `existingPeriods` array.
   * 2. Determines the field type ('actual' or 'budget') based on the period type.
   * 3. Retrieves the existing periods for the determined field.
   * 4. Maps each period to an object containing the year and month.
   * 5. Pushes each year and month object to the `existingPeriods` array.
   */
  private setExistingPeriods(): void {
    this.periodsFromDB = [];
    const field = this.periodType === MetricDataRole.ACTUAL ? 'actual' : 'budget';
    const existingPeriods = this.periods[field].map((period) => period.period);
    existingPeriods.forEach((period) => {
      const yearAndMonth: MetricPeriodYearAndMonth = {
        year: new Date(period).getFullYear(),
        month: new Date(period).getMonth(),
      };
      this.periodsFromDB.push(yearAndMonth);
    });
  }

  /**
   * Prepares periods to be saved:
   * checks that period was selected in the modal and maps to the LocalDate format.
   *
   * @return parsed LocalDate periods.
   */
  private preparePeriods(): LocalDate[] {
    const newPeriods: LocalDate[] = [];
    this.displayedPeriods.forEach((year) => {
      year.months.forEach((month) => {
        if (month.selected && !month.disabled) {
          newPeriods.push(DateService.hyphenDate(year.year, month.getNumber() + 1, 1));
        }
      });
    });
    return newPeriods;
  }

  /**
   * Prepares the list of companies for a checkbox selector.
   * This method initializes two arrays:
   * 1. `companiesForSelector`: A list of `CheckBoxSelector` objects representing companies available for selection.
   * 2. `selectedCompanies`: A list of pre-selected `CheckBoxSelector` objects,
   * which will include the company if there is only one in the input.
   */
  private prepareCompaniesForSelector(): void {
    this.companiesForSelector = [];
    this.selectedCompanies = [];
    // prepare companies for selector in case of calling modal from single company component
    if (this.companies.length === 1) {
      this.companiesForSelector.push(
        {
          UUID: this.companies[0].uuid,
          item: this.companies[0].name,
          chosen: true,
        }
      );
      this.selectedCompanies.push(this.companiesForSelector[0]);
      return;
    }

    // prepare companies for selector in case of calling modal from bulk request
    this.companies
      .filter((company) => company.isTracked)
      .forEach((company) => this.companiesForSelector.push(
        {
          UUID: company.uuid,
          item: company.name,
          chosen: false,
        }
      ));
  }

  private resetData(): void {
    this.displayedPeriods = null;
    this.selectedPeriod = null;
    this.periodTypeForSelector = null;
    this.startingYears = null;
    this.endingYears = null;
    this.displayedPeriods = null;
    this.selected = 'none';
    this.allSelectedDisabled = false;
    this.showPeriods = null;
    this.periodsFromDB = null;
  }
}

export type ModalManagePeriodParent = 'bulk-companies' | 'single-company';

class YearHelper {
  private readonly quarterMonths = [2, 5, 8, 11];

  isAllSelected(year: MetricYearPlanning): boolean {
    return year.months
      .filter((month) => !month.disabled)
      .every((month) => month.selected);
  }

  selectQuartersElseAllMonths(year: MetricYearPlanning): void {
    const nonDisabledMonths = year.months
      .filter((month) => !month.disabled);
    const filteredByQuarterMonths = nonDisabledMonths
      .filter((month) => this.quarterMonths.includes(month.getNumber()));

    if (filteredByQuarterMonths.length) {
      filteredByQuarterMonths.forEach((month) => month.selected = true);
    } else {
      nonDisabledMonths.forEach((month) => month.selected = true);
    }
  }

  isQuarterMissing(year: MetricYearPlanning): boolean {
    return !year.months
      .filter((month) => !month.disabled && this.quarterMonths.includes(month.getNumber()))
      .every((month) => month.selected);
  }
}
